import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons'
import {
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Layout,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Upload,
} from 'antd'
import { Content, Header } from 'antd/es/layout/layout'
import type { SegmentedValue } from 'antd/es/segmented'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd/es/upload'
import { isAxiosError } from 'axios'
import { useNavigate, useParams } from 'react-router-dom'
import styles from './Shop.module.scss'
import Loading from '../../../components/Loading/Loading'
import uploadImage from '../../../images/upload.svg'
import {
  deleteProductAsync,
  getInventoryMatrixAsync,
  getProductsAsync,
  getStudiosAsync,
  IShopProduct,
  ICreateProductDto,
  postProductAsync,
  patchProduct,
  putProductAsync,
  ProductStatus,
  getSignedUrlAsync,
  setStudioInventoryAsync,
} from '../../../redux/admin/shop'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { StatusEnum } from '../../../redux/type'
import {
  defaultExtensionForKind,
  extensionForContentType,
  getFileExtension,
  IMAGE_UPLOAD_ACCEPT,
  resolveMediaContentType,
  validateMediaFileForKind,
  VIDEO_UPLOAD_ACCEPT,
} from '../../../utils/mediaMime'
import { notifyError, notifySuccess } from '../../../utils/Notification'
import { normalizeGcsPublicUrl, uploadFileToSignedUrl } from '../../../utils/uploadToSignedUrl'

const STATUS_COLORS: Record<ProductStatus, string> = {
  available: 'green',
  sold_out: 'red',
  not_available: 'default',
  coming_soon: 'blue',
}

const CATEGORY_COLORS: Record<string, string> = {
  supplement: 'cyan',
  accessory: 'purple',
}

const STATUS_LABELS: Record<ProductStatus, string> = {
  available: 'Available',
  sold_out: 'Sold Out',
  not_available: 'Not Available',
  coming_soon: 'Coming Soon',
}

const dollars = (cents?: number) => {
  const c = cents || 0
  const whole = Math.floor(c / 100)
  const frac = c % 100
  if (frac === 0) return String(whole)
  return `${whole}.${String(frac).padStart(2, '0')}`
}

type MediaValue = string | File | null | undefined

const mediaToFileList = (media: MediaValue, uid: string, fallbackName: string): UploadFile[] => {
  if (media === null || media === undefined) return []
  if (typeof media === 'string') {
    if (!media) return []
    return [{ uid, name: media.split('/').pop() || fallbackName, status: 'done', url: media }]
  }
  return [
    {
      uid,
      name: media.name || fallbackName,
      status: 'done' as const,
      originFileObj: media as UploadFile['originFileObj'],
    },
  ]
}

const prepareUploadFile = (file: File, kind: 'image' | 'video'): File => {
  const type = resolveMediaContentType(file, kind)
  const ext = getFileExtension(file.name) || extensionForContentType(type, kind)
  const name = file.name?.trim() || `${kind}-${Date.now()}.${ext || defaultExtensionForKind(kind)}`
  if (file.name === name && file.type === type) return file
  return new File([file], name, { type, lastModified: file.lastModified })
}

const saveErrorMessage = (e: unknown): string => {
  if (typeof e === 'string') return e
  if (isAxiosError(e)) {
    if (!e.response) return e.message || 'Network error while uploading media'
    const data = e.response.data as { message?: string | string[] } | undefined
    if (data && Array.isArray(data.message)) return data.message.join(', ')
    if (data && typeof data.message === 'string') return data.message
    return e.message || 'Request failed'
  }
  if (e instanceof Error) return e.message
  return 'Could not save product'
}

const pickMediaFile = (file?: UploadFile): File | null => {
  if (!file) return null
  if (file instanceof File) return file
  const origin = file.originFileObj
  if (origin instanceof File) return origin
  return null
}

const pickMediaFromChange = (file: UploadFile, fileList: UploadFile[]): File | null =>
  pickMediaFile(fileList[fileList.length - 1]) ?? pickMediaFile(file)

const useMediaPreviewUrl = (media: MediaValue) => {
  const previewUrl = useMemo(() => {
    if (!media || media === null) return null
    if (typeof media === 'string') return media
    return URL.createObjectURL(media)
  }, [media])

  useEffect(() => {
    if (media instanceof File && previewUrl?.startsWith('blob:')) {
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [media, previewUrl])

  return previewUrl
}

interface ProductImageUploadProps {
  imageMedia: MediaValue
  fileList: UploadFile[]
  onPick: (file: File) => void
  onClear: () => void
}

const ProductImageUpload = ({ imageMedia, fileList, onPick, onClear }: ProductImageUploadProps) => {
  const previewUrl = useMediaPreviewUrl(imageMedia)

  return (
    <Form.Item label="Product Image">
      {previewUrl && (
        <div className={styles.productMediaPreview}>
          <img src={previewUrl} alt="Product preview" />
        </div>
      )}
      <Upload
        accept={IMAGE_UPLOAD_ACCEPT}
        className={styles.customUpload}
        multiple={false}
        maxCount={1}
        beforeUpload={() => false}
        fileList={fileList}
        onChange={(info) => {
          if (info.file.status === 'removed') {
            onClear()
            return
          }
          const picked = pickMediaFromChange(info.file, info.fileList)
          if (picked) onPick(picked)
        }}
        onRemove={() => {
          onClear()
          return true
        }}
      >
        <div className={styles.uploadTrigger}>
          <span>{previewUrl ? 'Replace Product Image' : 'Upload Product Image'}</span>
          <img src={uploadImage} alt="" />
        </div>
      </Upload>
    </Form.Item>
  )
}

interface ProductVideoUploadProps {
  videoMedia: MediaValue
  fileList: UploadFile[]
  onPick: (file: File) => void
  onClear: () => void
}

const ProductVideoUpload = ({ videoMedia, fileList, onPick, onClear }: ProductVideoUploadProps) => {
  const previewUrl = useMediaPreviewUrl(videoMedia)

  return (
    <Form.Item label="Product Video">
      {previewUrl && (
        <div className={styles.productMediaPreview}>
          <video src={previewUrl} controls preload="metadata" />
        </div>
      )}
      <Upload
        accept={VIDEO_UPLOAD_ACCEPT}
        className={styles.customUpload}
        multiple={false}
        maxCount={1}
        beforeUpload={() => false}
        fileList={fileList}
        onChange={(info) => {
          if (info.file.status === 'removed') {
            onClear()
            return
          }
          const picked = pickMediaFromChange(info.file, info.fileList)
          if (picked) onPick(picked)
        }}
        onRemove={() => {
          onClear()
          return true
        }}
      >
        <div className={styles.uploadTrigger}>
          <span>{previewUrl ? 'Replace Product Video' : 'Upload Product Video'}</span>
          <img src={uploadImage} alt="" />
        </div>
      </Upload>
    </Form.Item>
  )
}

const shopLoadingIndicator = <Loading style={{ background: 'none' }} />

interface ProductDeleteConfirmModalProps {
  showModal: boolean
  title: string
  isLoading: boolean
  onConfirm: () => void
  closeModal: () => void
}

const ProductDeleteConfirmModal = ({
  showModal,
  title,
  isLoading,
  onConfirm,
  closeModal,
}: ProductDeleteConfirmModalProps) => (
  <Modal open={showModal} centered closable={false} mask maskClosable={!isLoading} footer={null} onCancel={closeModal}>
    <div className={styles.confirmModal}>
      <div className={styles.confirmModalTitle}>{title}</div>
      <Flex gap={12} justify="center" className={styles.confirmModalActions}>
        <Button className={styles.confirmModalCancel} disabled={isLoading} onClick={closeModal}>
          No
        </Button>
        <Button className={styles.confirmModalConfirm} disabled={isLoading} onClick={onConfirm}>
          {isLoading ? <Spin indicator={<LoadingOutlined style={{ color: '#fffeff' }} spin />} /> : 'Yes'}
        </Button>
      </Flex>
    </div>
  </Modal>
)

export const Shop = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { tab } = useParams()
  const {
    products,
    matrix,
    studios,
    productsStatus,
    matrixStatus,
    error: apiError,
  } = useAppSelector((s) => s.admin.shop)
  const view: 'Products' | 'Inventory' = tab === 'inventory' ? 'Inventory' : 'Products'
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<IShopProduct | null>(null)
  const [saving, setSaving] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [productToDelete, setProductToDelete] = useState<IShopProduct | null>(null)
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const [imageMedia, setImageMedia] = useState<MediaValue>(undefined)
  const [videoMedia, setVideoMedia] = useState<MediaValue>(undefined)
  const imageMediaRef = useRef<MediaValue>(undefined)
  const videoMediaRef = useRef<MediaValue>(undefined)
  const pendingImageFileRef = useRef<File | null | undefined>(undefined)
  const pendingVideoFileRef = useRef<File | null | undefined>(undefined)
  const [form] = Form.useForm()

  const setImageMediaState = useCallback((value: MediaValue) => {
    imageMediaRef.current = value
    setImageMedia(value)
  }, [])

  const setVideoMediaState = useCallback((value: MediaValue) => {
    videoMediaRef.current = value
    setVideoMedia(value)
  }, [])

  const pickImageFile = useCallback(
    (file: File) => {
      const error = validateMediaFileForKind(file, 'image')
      if (error) {
        notifyError('Invalid file', error)
        return
      }
      const prepared = prepareUploadFile(file, 'image')
      pendingImageFileRef.current = prepared
      setImageMediaState(prepared)
    },
    [setImageMediaState],
  )

  const pickVideoFile = useCallback(
    (file: File) => {
      const error = validateMediaFileForKind(file, 'video')
      if (error) {
        notifyError('Invalid file', error)
        return
      }
      const prepared = prepareUploadFile(file, 'video')
      pendingVideoFileRef.current = prepared
      setVideoMediaState(prepared)
    },
    [setVideoMediaState],
  )

  const clearImageMedia = useCallback(() => {
    pendingImageFileRef.current = null
    setImageMediaState(null)
  }, [setImageMediaState])

  const clearVideoMedia = useCallback(() => {
    pendingVideoFileRef.current = null
    setVideoMediaState(null)
  }, [setVideoMediaState])

  const resetMediaState = useCallback(() => {
    pendingImageFileRef.current = undefined
    pendingVideoFileRef.current = undefined
    setImageMediaState(undefined)
    setVideoMediaState(undefined)
  }, [setImageMediaState, setVideoMediaState])

  const imageFileList = useMemo(() => mediaToFileList(imageMedia, 'product-image', 'product-image'), [imageMedia])
  const videoFileList = useMemo(() => mediaToFileList(videoMedia, 'product-video', 'product-video'), [videoMedia])

  const uploadProductMedia = async (productId: string, file: File, kind: 'image' | 'video') => {
    const validationError = validateMediaFileForKind(file, kind)
    if (validationError) {
      throw new Error(validationError)
    }
    const uploadFile = prepareUploadFile(file, kind)
    const contentType = resolveMediaContentType(uploadFile, kind)
    const { uploadUrl, publicUrl } = await dispatch(
      getSignedUrlAsync({
        id: productId,
        fileName: uploadFile.name,
        contentType,
        kind,
      }),
    ).unwrap()
    await uploadFileToSignedUrl(uploadUrl, uploadFile, contentType)
    return normalizeGcsPublicUrl(publicUrl)
  }

  const refresh = useCallback(() => {
    dispatch(getProductsAsync())
    dispatch(getInventoryMatrixAsync())
  }, [dispatch])

  useEffect(() => {
    if (!tab) {
      navigate('/admin/shop/products', { replace: true })
    }
  }, [tab, navigate])

  useEffect(() => {
    refresh()
    dispatch(getStudiosAsync())
  }, [dispatch, refresh])

  useEffect(() => {
    if (productsStatus === StatusEnum.Failed) {
      notifyError('Unable to load products', apiError || 'Failed to load products')
    }
  }, [productsStatus, apiError])

  useEffect(() => {
    if (matrixStatus === StatusEnum.Failed) {
      notifyError('Unable to load inventory', apiError || 'Failed to load inventory')
    }
  }, [matrixStatus, apiError])

  const openCreate = () => {
    setEditing(null)
    resetMediaState()
    form.resetFields()
    form.setFieldsValue({
      category: 'supplement',
      status: 'available',
      active: true,
      lowStockThreshold: 5,
      sortOrder: 0,
    })
    setDrawerOpen(true)
  }

  const openEdit = (p: IShopProduct) => {
    setEditing(p)
    const stockByStudio: Record<string, number> = {}
    ;(p.inventory || []).forEach((i) => (stockByStudio[i.clinicId] = i.stock))
    const { imageUrl, videoUrl, inventory, totalStock, ...rest } = p
    pendingImageFileRef.current = undefined
    pendingVideoFileRef.current = undefined
    setImageMediaState(imageUrl || undefined)
    setVideoMediaState(videoUrl || undefined)
    form.setFieldsValue({
      ...rest,
      price: dollars(p.price),
      stock: stockByStudio,
    })
    setDrawerOpen(true)
  }

  const toggleActive = async (p: IShopProduct, active: boolean) => {
    const previousActive = p.active
    dispatch(patchProduct({ id: p.id, changes: { active } }))
    try {
      await dispatch(putProductAsync({ id: p.id, data: { active } })).unwrap()
      notifySuccess('Updated', active ? 'Product enabled' : 'Product disabled')
    } catch {
      dispatch(patchProduct({ id: p.id, changes: { active: previousActive } }))
      notifyError('Error', 'Could not update product')
    }
  }

  const closeDrawer = () => {
    if (saving) return
    form.resetFields()
    resetMediaState()
    setEditing(null)
    setDrawerOpen(false)
  }

  const onSave = async (values: Record<string, any>) => {
    const stock = values.stock || {}
    const pendingImage = pendingImageFileRef.current
    const pendingVideo = pendingVideoFileRef.current

    const payload: ICreateProductDto = {
      name: values.name,
      category: values.category,
      description: values.description,
      suggestedUse: values.suggestedUse,
      highlight: values.highlight,
      quantityLabel: values.quantityLabel,
      price: Math.round(parseFloat(values.price || '0') * 100),
      status: values.status,
      active: values.active,
      sortOrder: values.sortOrder ?? 0,
    }
    if (values.lowStockThreshold != null) payload.lowStockThreshold = Number(values.lowStockThreshold)

    try {
      setSaving(true)
      let productId = editing?.id
      if (editing) {
        await dispatch(putProductAsync({ id: editing.id, data: payload })).unwrap()
      } else {
        const created: any = await dispatch(postProductAsync({ data: payload })).unwrap()
        productId = created?.id
      }

      if (productId) {
        const mediaUpdates: Partial<ICreateProductDto> = {}

        if (pendingImage === null) {
          mediaUpdates.imageUrl = ''
        } else if (pendingImage instanceof File) {
          mediaUpdates.imageUrl = await uploadProductMedia(productId, pendingImage, 'image')
        }

        if (pendingVideo === null) {
          mediaUpdates.videoUrl = ''
        } else if (pendingVideo instanceof File) {
          mediaUpdates.videoUrl = await uploadProductMedia(productId, pendingVideo, 'video')
        }

        if (Object.keys(mediaUpdates).length) {
          await dispatch(putProductAsync({ id: productId, data: mediaUpdates })).unwrap()
        }

        const items = studios
          .filter((st) => stock[st.id] !== undefined && stock[st.id] !== null && stock[st.id] !== '')
          .map((st) => ({ clinicId: st.id, stock: Math.max(0, Math.round(Number(stock[st.id]))) }))
        if (items.length) {
          await dispatch(setStudioInventoryAsync({ id: productId, items })).unwrap()
        }
      }

      notifySuccess('Saved', 'Product saved')
      form.resetFields()
      resetMediaState()
      setEditing(null)
      setDrawerOpen(false)
      refresh()
    } catch (e: unknown) {
      notifyError('Error', saveErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return
    try {
      setIsDeleteLoading(true)
      await dispatch(deleteProductAsync({ id: productToDelete.id })).unwrap()
      notifySuccess('Deleted', `${productToDelete.name} removed`)
      setShowDeleteModal(false)
      setProductToDelete(null)
      refresh()
    } catch {
      notifyError('Error', 'Could not delete product')
    } finally {
      setIsDeleteLoading(false)
    }
  }

  const handleTabChange = (value: SegmentedValue) => {
    navigate(`/admin/shop/${value.toString().toLowerCase()}`)
  }

  const productColumns: ColumnsType<IShopProduct> = [
    {
      title: '',
      dataIndex: 'imageUrl',
      width: 72,
      render: (url) => (url ? <img src={url} alt="" className={styles.productThumb} /> : null),
    },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (c) => <Tag color={CATEGORY_COLORS[c] || 'geekblue'}>{c}</Tag>,
      // filters: [
      //   { text: 'Supplement', value: 'supplement' },
      //   { text: 'Accessory', value: 'accessory' },
      // ],
      // onFilter: (v, r) => r.category === v,
    },
    { title: 'Price', dataIndex: 'price', render: (p) => `$${dollars(p)}` },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: ProductStatus) =>
        s === 'not_available' ? (
          <Tag className={styles.mutedTag}>{STATUS_LABELS[s]}</Tag>
        ) : (
          <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>
        ),
    },
    { title: 'Low-stock at', dataIndex: 'lowStockThreshold' },
    { title: 'Total stock', dataIndex: 'totalStock' },
    {
      title: 'Active',
      dataIndex: 'active',
      render: (active, r) => (
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <Switch checked={active} onChange={(v) => toggleActive(r, v)} />
        </span>
      ),
    },
    {
      title: '',
      width: 48,
      render: (_, r) => (
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <DeleteOutlined
            className={styles.deleteIcon}
            onClick={() => {
              setProductToDelete(r)
              setShowDeleteModal(true)
            }}
          />
        </span>
      ),
    },
  ]

  const matrixColumns: ColumnsType<any> = useMemo(() => {
    const base: ColumnsType<any> = [{ title: 'Product', dataIndex: 'name' }]
    studios.forEach((st) => {
      base.push({
        title: st.name,
        render: (_: any, row: any) => {
          const cell = row.studios?.[st.id]
          if (!cell) return <Tag className={styles.mutedTag}>—</Tag>
          return (
            <Tag color={cell.studioStatus === 'sold_out' ? 'red' : cell.lowStock ? 'orange' : 'green'}>
              {cell.stock}
            </Tag>
          )
        },
      })
    })
    base.push({
      title: 'Total',
      render: (_: any, row: any) =>
        Object.values(row.studios || {}).reduce((sum: number, cell: any) => sum + (cell?.stock ?? 0), 0),
    })
    return base
  }, [studios])

  return (
    <Layout>
      <Header style={{ borderBottom: '1px solid #2D2C3C' }} />
      <Content>
        <div className={styles.tabBar}>
          <Segmented options={['Products', 'Inventory']} value={view} onChange={handleTabChange} />
          {view === 'Products' && (
            <Button type="primary" onClick={openCreate}>
              Add Product
            </Button>
          )}
        </div>
        <div className={styles.page}>
          {view === 'Products' ? (
            productsStatus === StatusEnum.Pending && products.length === 0 ? (
              shopLoadingIndicator
            ) : (
              <Table
                rootClassName={styles.tableRoot}
                className={styles.table}
                rowKey="id"
                columns={productColumns}
                dataSource={products}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => openEdit(record),
                  className: styles.clickableRow,
                })}
              />
            )
          ) : matrixStatus === StatusEnum.Pending && matrix.length === 0 ? (
            shopLoadingIndicator
          ) : (
            <Table
              rootClassName={styles.tableRoot}
              className={styles.table}
              rowKey="productId"
              columns={matrixColumns}
              dataSource={matrix}
              pagination={false}
            />
          )}
        </div>

        <Drawer
          title={editing ? 'Edit Product' : 'Add Product'}
          closable={false}
          width={400}
          classNames={{ body: styles.productDrawerBody }}
          extra={
            <Space>
              <div className={styles.closeContainer} onClick={closeDrawer} />
            </Space>
          }
          open={drawerOpen}
        >
          {drawerOpen && (
            <div className={styles.productForm}>
              <Form
                form={form}
                layout="vertical"
                className={styles.productFormFields}
                requiredMark={false}
                onFinish={onSave}
              >
                <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                  <Input placeholder="Enter product name" />
                </Form.Item>
                <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                  <Select
                    placeholder="Select category"
                    suffixIcon={null}
                    options={[
                      { value: 'supplement', label: 'Supplement' },
                      { value: 'accessory', label: 'Accessory' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="price" label="Price (USD)" rules={[{ required: true, message: 'Price is required' }]}>
                  <Input className={styles.productField} prefix="$" type="number" step="0.01" placeholder="0.00" />
                </Form.Item>
                <Form.Item name="quantityLabel" label="Quantity label (e.g. 30 Capsules)">
                  <Input className={styles.productField} placeholder="30 Capsules" />
                </Form.Item>
                <Form.Item name="highlight" label="Highlight (short card text)">
                  <Input className={styles.productField} placeholder="Short highlight" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                  <Input.TextArea rows={3} placeholder="Product description" />
                </Form.Item>
                <Form.Item name="suggestedUse" label="Suggested Use">
                  <Input.TextArea rows={3} placeholder="Suggested use" />
                </Form.Item>
                <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                  <Select
                    placeholder="Select status"
                    suffixIcon={null}
                    options={(Object.keys(STATUS_LABELS) as ProductStatus[]).map((s) => ({
                      value: s,
                      label: STATUS_LABELS[s],
                    }))}
                  />
                </Form.Item>
                <Form.Item name="lowStockThreshold" label="Low-stock threshold">
                  <InputNumber min={0} controls={false} className={styles.productNumberInput} />
                </Form.Item>
                <Form.Item
                  name="sortOrder"
                  label="Sort order"
                  tooltip="Lower numbers appear first in the mobile carousel"
                >
                  <InputNumber min={0} controls={false} className={styles.productNumberInput} />
                </Form.Item>
                <Form.Item name="active" label="Active" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <ProductImageUpload
                  imageMedia={imageMedia}
                  fileList={imageFileList}
                  onPick={pickImageFile}
                  onClear={clearImageMedia}
                />
                <ProductVideoUpload
                  videoMedia={videoMedia}
                  fileList={videoFileList}
                  onPick={pickVideoFile}
                  onClear={clearVideoMedia}
                />

                <h4 className={styles.sectionTitle}>Per-studio stock</h4>
                {studios.map((st) => (
                  <Form.Item key={st.id} name={['stock', st.id]} label={st.name}>
                    <InputNumber min={0} controls={false} className={styles.productNumberInput} />
                  </Form.Item>
                ))}

                <Form.Item className={styles.productFormActions}>
                  <div className={styles.footer}>
                    <Button className={styles.button} type="default" onClick={closeDrawer}>
                      Cancel
                    </Button>
                    <Button className={styles.button} htmlType="submit" type="primary" loading={saving}>
                      Save
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </div>
          )}
        </Drawer>

        <ProductDeleteConfirmModal
          showModal={showDeleteModal}
          title={productToDelete ? `Delete ${productToDelete.name}?` : 'Delete this product?'}
          isLoading={isDeleteLoading}
          onConfirm={handleDeleteConfirm}
          closeModal={() => {
            if (!isDeleteLoading) {
              setShowDeleteModal(false)
              setProductToDelete(null)
            }
          }}
        />
      </Content>
    </Layout>
  )
}

export default Shop
